import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as api from "../api/client";
import type { SleepState, User } from "../api/client";
import { CurrencyIcon } from "./CurrencyIcon";
import { HeartIcon } from "./HeartIcon";
import { Logo } from "./Logo";
import { VolumePanel } from "./VolumePanel";
import { formatGold, formatVoidJuice } from "../logic/currency";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn, ghostBtn, primaryBtn } from "../theme/styles";

type Theme = typeof pixelTheme;

interface Props {
  user: User | null;
  /** Optional back button. `backTo` is a router path; `backLabel`
   *  defaults to "◀ BACK". Used by deep-nested pages (the world
   *  tree, primarily) where the right-side nav alone doesn't
   *  capture "up one level". Pages already at a top-level
   *  destination (Lobby, the World root) omit this. */
  backTo?: string;
  backLabel?: string;
  /** Custom click handler for the back button. Takes precedence over
   *  `backTo`. Use when the page needs to gate navigation (e.g. an
   *  unsaved-changes prompt) or pop history instead of routing to a
   *  fixed path. */
  onBack?: () => void;
  /** Optional logout handler. If omitted, header issues
   *  `api.logout()` itself and routes to /login. */
  onLogout?: () => void;
}

// Single header component used on every authed page (Lobby, World,
// Inventory, Collection, …). Shows the logo (a link back to the
// lobby), optional back
// button, both currency chips at the same height, and a hamburger
// menu that holds the nav links (World, Inventory, Collection,
// Admin), volume sliders, and Log Out. The header must stay one row
// — page breadcrumbs render below the header via the `Breadcrumb`
// component so a long path can't force the player name / HP / coins
// to wrap.
export function AppHeader({
  user,
  backTo,
  backLabel,
  onBack,
  onLogout,
}: Props) {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click / Escape so the menu doesn't trap focus.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (menuRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  async function defaultLogout() {
    try {
      await api.logout();
    } catch {
      // Ignore — clearing the session locally still navigates the
      // user away.
    }
    navigate("/login", { replace: true });
  }

  const handleLogout = onLogout ?? (() => void defaultLogout());

  const goto = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  // Menu items in spec order: World (distinct), then the rest, then
  // Log Out (red). Admin only when the user has admin rights.
  const menuItemStyle: React.CSSProperties = {
    ...ghostBtn(theme).style,
    width: "100%",
    textAlign: "left",
    justifyContent: "flex-start",
  };
  const worldItemStyle: React.CSSProperties = {
    ...primaryBtn(theme).style,
    width: "100%",
    textAlign: "left",
    justifyContent: "flex-start",
  };
  const logoutItemStyle: React.CSSProperties = {
    ...menuItemStyle,
    color: E32.rust,
  };

  return (
    <header
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        rowGap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {(onBack || backTo) && (
          <button
            onClick={onBack ?? (() => navigate(backTo!))}
            {...defaultBtn(theme)}
          >
            {backLabel ?? "◀ BACK"}
          </button>
        )}
        {/* The logo doubles as the "home" link — every authed page's
            way back to the lobby. Pages therefore never need their
            own "◀ LOBBY" back button. */}
        <Link
          to="/lobby"
          aria-label="Deck of Infinity — go to lobby"
          style={{ textDecoration: "none", display: "flex" }}
        >
          <Logo />
        </Link>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 8,
          flexWrap: "wrap",
          rowGap: 6,
        }}
      >
        {user && (
          <span
            style={{
              alignSelf: "center",
              fontFamily: theme.fontBody,
              fontSize: 16,
              color: theme.inkDim,
              whiteSpace: "nowrap",
            }}
          >
            {user.username}
          </span>
        )}

        {user && (
          <>
            <HpChip sleep={user.sleep} theme={theme} />
            <CurrencyChip kind="juice" amount={user.void_juice_ml} theme={theme} />
            <CurrencyChip kind="gold" amount={user.gold_coins} theme={theme} />
          </>
        )}

        <div style={{ position: "relative", display: "flex" }}>
          <button
            ref={triggerRef}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Menu"
            {...ghostBtn(theme)}
            style={{ ...ghostBtn(theme).style, height: "100%" }}
          >
            ☰
          </button>
          {menuOpen && (
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                minWidth: 220,
                background: theme.panelSolid,
                border: `2px solid ${theme.panelEdgeStrong}`,
                padding: 6,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                zIndex: 100,
                boxShadow: `4px 4px 0 0 ${theme.panelEdgeStrong}`,
              }}
            >
              <button
                role="menuitem"
                onClick={() => goto("/world")}
                className="doi-btn doi-btn-primary"
                style={worldItemStyle}
              >
                WORLD
              </button>
              <button
                role="menuitem"
                onClick={() => goto("/inventory")}
                className="doi-btn doi-btn-ghost"
                style={menuItemStyle}
              >
                INVENTORY
              </button>
              <button
                role="menuitem"
                onClick={() => goto("/collection")}
                className="doi-btn doi-btn-ghost"
                style={menuItemStyle}
              >
                COLLECTION
              </button>
              {user?.is_admin && (
                <button
                  role="menuitem"
                  onClick={() => goto("/admin/cards")}
                  className="doi-btn doi-btn-ghost"
                  style={menuItemStyle}
                >
                  ADMIN
                </button>
              )}
              {user && !user.is_guest && (
                <button
                  role="menuitem"
                  onClick={() => goto("/account/change-password")}
                  className="doi-btn doi-btn-ghost"
                  style={menuItemStyle}
                >
                  CHANGE PASSWORD
                </button>
              )}
              <div
                style={{
                  padding: "4px 2px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <VolumePanel mode="stacked" />
              </div>
              <button
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  handleLogout();
                }}
                className="doi-btn doi-btn-ghost"
                style={logoutItemStyle}
              >
                LOG OUT
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// Breadcrumb / page-title strip rendered below the header. Lives
// outside `AppHeader` so a long path can't force the header row to
// wrap — pages drop it in between the header and the page content.
export function Breadcrumb({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  const theme = pixelTheme;
  return (
    <div
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 11,
        letterSpacing: theme.letterSpacing,
        color: theme.inkFaint,
        textTransform: "uppercase",
      }}
    >
      {text}
    </div>
  );
}

// HP chip — same outer dimensions as the currency chips. Animates
// live HP locally when the user is sleeping at the Inn (closed-form
// math, no polling); idle when awake. The full-HP state shows green;
// damaged shows rust (matches the in-battle PlayerPanel threshold).
function HpChip({ sleep, theme }: { sleep: SleepState; theme: Theme }) {
  const [hp, setHp] = useState(() => api.liveHp(sleep));

  useEffect(() => {
    setHp(api.liveHp(sleep));

    if (!sleep.sleeping) return;

    const id = window.setInterval(() => {
      const next = api.liveHp(sleep);
      setHp(next);
      // Once we hit max, stop ticking — the actual wake will arrive
      // via a fresh user payload from /api/me on the next nav.
      if (next >= sleep.max_hp) {
        window.clearInterval(id);
      }
    }, 250);

    return () => window.clearInterval(id);
  }, [sleep]);

  const damaged = hp < sleep.max_hp;

  return (
    <div
      title={`HP: ${hp} / ${sleep.max_hp}${sleep.sleeping ? " (resting)" : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        background: damaged ? E32.rust : E32.forest,
        color: E32.parchment,
        border: `2px solid ${theme.panelEdgeStrong}`,
        fontFamily: theme.fontDisplay,
        fontSize: 10,
        lineHeight: 1,
        letterSpacing: theme.letterSpacing,
        whiteSpace: "nowrap",
        textShadow: `1px 1px 0 ${theme.panelEdgeStrong}`,
        boxSizing: "border-box",
      }}
    >
      <HeartIcon size={12} />
      <span>{hp}/{sleep.max_hp}</span>
    </div>
  );
}

// Both chips share the exact same outer dimensions (padding +
// border + height) so they line up next to ghost-button nav. Only
// the fill / glyph / text shadow differ.
function CurrencyChip({
  kind,
  amount,
  theme,
}: {
  kind: "juice" | "gold";
  amount: number;
  theme: Theme;
}) {
  const isJuice = kind === "juice";
  const label = isJuice ? formatVoidJuice(amount) : formatGold(amount);
  const title = isJuice
    ? `Void Juice: ${amount} mL`
    : `Gold: ${amount} coins`;

  return (
    <div
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        // Match `ghostBtn` padding so chip outer height === button
        // outer height. ghostBtn is `6px 10px` + `2px solid` border.
        padding: "6px 10px",
        background: isJuice ? E32.mulberry : E32.amber,
        color: isJuice ? E32.parchment : E32.ink,
        border: `2px solid ${theme.panelEdgeStrong}`,
        fontFamily: theme.fontDisplay,
        fontSize: 10,
        lineHeight: 1,
        letterSpacing: theme.letterSpacing,
        whiteSpace: "nowrap",
        textShadow: isJuice ? `1px 1px 0 ${theme.panelEdgeStrong}` : "none",
        boxSizing: "border-box",
      }}
    >
      <CurrencyIcon kind={isJuice ? "juice" : "gold"} size={16} />
      <span>{label}</span>
    </div>
  );
}
